import type { Graph, ItemRow } from '@/schema/estate';
import { aliasesOf, getItem, itemsByEstate } from './itemService';

const prefixOf = (number: string) => (number.split('-')[0] ?? '').toUpperCase();

function sequenceOf(number: string): number | null {
  const seq = number.split('-')[1] ?? '';
  return /^\d+$/.test(seq) ? Number(seq) : null;
}

const formatNumber = (prefix: string, seq: number) => `${prefix}-${String(seq).padStart(4, '0')}`;

/** Aliases count so a retired number is never reissued. */
function highestSequence(rows: ItemRow[], prefix: string): number {
  let max = 0;
  for (const row of rows) {
    for (const number of [row.inventory_number, ...aliasesOf(row)]) {
      const seq = sequenceOf(number);
      if (seq !== null && prefixOf(number) === prefix && seq > max) max = seq;
    }
  }
  return max;
}

function aliasesWith(row: ItemRow, retired: string): string[] {
  const prior = aliasesOf(row);
  return !retired || prior.includes(retired) ? prior : [...prior, retired];
}

// The store applies writes asynchronously, so numbering runs one task at a time and each
// write waits until it reads back; otherwise the next max+1 would read a stale max.
let queue: Promise<unknown> = Promise.resolve();

function serialized<T>(task: () => Promise<T>): Promise<T> {
  const run = queue.then(task);
  queue = run.catch(() => undefined);
  return run;
}

async function readBack(done: () => boolean, what: string) {
  const deadline = Date.now() + 15000;
  while (!done()) {
    if (Date.now() >= deadline) throw new Error(`${what} did not read back within 15s`);
    await new Promise((r) => setTimeout(r, 50));
  }
}

async function writeNumber(g: Graph, id: string, number: string, aliases: string[]) {
  await g.item.update(id, {
    inventory_number: number,
    number_aliases: aliases,
    updated_at: Date.now(),
  });
  await readBack(
    () => g.item(id)?.$data.inventory_number === number,
    `number ${number} for item ${id}`,
  );
}

/**
 * Numbers the item `PREFIX-NNNN`, one past the highest sequence the estate has used under
 * that prefix. A number that already has the prefix is kept; a replaced one becomes an alias.
 */
export function assignNumber(g: Graph, itemId: string, prefix: string): Promise<void> {
  return serialized(async () => {
    const want = prefix.trim().toUpperCase();
    if (!want) return;
    // Items are numbered right after they are created, often before the create reads back.
    await readBack(() => g.item(itemId)?.$data !== undefined, `item ${itemId}`);
    const row = getItem(g, itemId);
    if (!row) throw new Error(`item ${itemId} is archived`);
    if (row.inventory_number && prefixOf(row.inventory_number) === want) return;
    const next = formatNumber(
      want,
      highestSequence(itemsByEstate(g, row.estate_id ?? ''), want) + 1,
    );
    await writeNumber(g, itemId, next, aliasesWith(row, row.inventory_number));
  });
}

function groupBy(rows: ItemRow[], key: (row: ItemRow) => string): Map<string, ItemRow[]> {
  const groups = new Map<string, ItemRow[]>();
  for (const row of rows) {
    const k = key(row);
    const group = groups.get(k);
    if (group) group.push(row);
    else groups.set(k, [row]);
  }
  return groups;
}

const byCreation = (a: ItemRow, b: ItemRow) =>
  a.created_at - b.created_at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Two devices numbering offline can issue the same number. Within each estate the earliest
 * created holder keeps it; every other holder gets the next free number in the prefix.
 * Returns how many items were renumbered.
 */
export function resolveNumberConflicts(g: Graph): Promise<number> {
  return serialized(async () => {
    const live = g.item.all().filter((row) => !row.archived);
    let changed = 0;
    for (const rows of groupBy(live, (row) => row.estate_id ?? '').values()) {
      const holders = groupBy(
        rows.filter((row) => row.inventory_number),
        (row) => row.inventory_number,
      );
      const shared = [...holders]
        .filter(([, list]) => list.length > 1)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
      const issued = new Map<string, number>();
      for (const [number, list] of shared) {
        const [, ...losers] = list.sort(byCreation);
        const prefix = prefixOf(number);
        for (const loser of losers) {
          const seq = (issued.get(prefix) ?? highestSequence(rows, prefix)) + 1;
          issued.set(prefix, seq);
          await writeNumber(g, loser.id, formatNumber(prefix, seq), aliasesWith(loser, number));
          changed += 1;
        }
      }
    }
    return changed;
  });
}
